import { Switch, Route, Redirect, useRoute, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth, AuthProvider } from "@/hooks/use-auth";
import Navigation from "@/components/navigation";
import ParticlesBackground from "@/components/particles-background";
import Home from "@/pages/home";
import Login from "@/pages/login";
import Earn from "@/pages/earn";
import { LootablyOfferwallPage } from "@/pages/earn/Lootably";
import SponsoredGames from "@/pages/earn/SponsoredGames";
import ClippingPage from "@/pages/earn/Clipping";
import Withdraw from "@/pages/withdraw";
import Profile from "@/pages/profile";
import Leaderboard from "@/pages/leaderboard";
import PromoCodes from "@/pages/promo-codes";
import NotFound from "@/pages/not-found";
import AdminPage from "@/pages/admin";
import Footer from "@/components/footer";

function AppRoutes() {
  const { user, isAuthLoading } = useAuth();
  const [isLoginPage] = useRoute("/login");

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  const isVerified = user?.isVerified || false;
  const isAdmin = user?.role === "admin";

  return (
    <>
      {!isLoginPage && <Navigation />}
      <main className={`flex-grow ${!isLoginPage ? "pt-16" : ""}`}>
          <Switch>
            {/* Public routes */}
            <Route path="/" component={Home} />

            {/* If already logged in, redirect /login --> / */}
            <Route path="/login">
              {user ? <Redirect to="/" /> : <Login />}
            </Route>

            {/* Protected routes (explicit guards) */}
            <Route path="/earn">
              {isVerified ? <Earn /> : <Redirect to="/login" />}
            </Route>

            <Route path="/earn/lootably">
              {isVerified ? <LootablyOfferwallPage /> : <Redirect to="/login" />}
            </Route>

            <Route path="/earn/sponsored-games">
              {isVerified ? <SponsoredGames /> : <Redirect to="/login" />}
            </Route>

            <Route path="/earn/clipping">
              {isVerified ? <ClippingPage /> : <Redirect to="/login" />}
            </Route>

            <Route path="/withdraw">
              {isVerified ? <Withdraw /> : <Redirect to="/login" />}
            </Route>

            <Route path="/profile">
              {isVerified ? <Profile /> : <Redirect to="/login" />}
            </Route>

            <Route path="/leaderboard">
              {isVerified ? <Leaderboard /> : <Redirect to="/login" />}
            </Route>

            <Route path="/promo-codes">
              {isVerified ? <PromoCodes /> : <Redirect to="/login" />}
            </Route>

            {/* Admin routes */}
            <Route path="/admin">
              {isAdmin ? <AdminPage /> : <Redirect to="/" />}
            </Route>

            {/* Fallback 404 route — keep this last */}
            <Route component={NotFound} />
          </Switch>
        </main>
      {!isLoginPage && <Footer />}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <div className="min-h-screen bg-background text-foreground flex flex-col">
            <ParticlesBackground />
            <Toaster />
            <AppRoutes />
          </div>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
